import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { FaCheckCircle, FaHammer, FaBuilding, FaTools, FaArrowRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Cta from '../../components/NewCallToAction';

const BRAND_BLUE = "#3b82f6";
const DARK_SLATE = "#0f172a";

const ServiceDetailPage = ({ title, subtitle, heroBg, description, highlights, coreCompetencies, faqs }) => {
  return (
    <main style={{ background: '#f8fafc', minHeight: '100vh', overflowX: 'hidden' }}>
      <Helmet>
        <title>{title} | Abraj Al Dhafna Qatar</title>
        <meta name="description" content={description.substring(0, 155)} />
        <link rel="canonical" href={`https://abrajaldhafna.com/services/${title.toLowerCase().replace(/ /g, '-')}`} />
      </Helmet>

      {/* Breadcrumb & Back */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '15px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px', display: 'flex', gap: '10px', alignItems: 'center', fontSize: '14px' }}>
          <Link to="/" style={{ color: '#64748b', textDecoration: 'none' }}>Home</Link>
          <span style={{ color: '#cbd5e1' }}>/</span>
          <Link to="/services" style={{ color: '#64748b', textDecoration: 'none' }}>Services</Link>
          <span style={{ color: '#cbd5e1' }}>/</span>
          <span style={{ color: DARK_SLATE, fontWeight: 600 }}>{title}</span>
        </div>
      </div>

      {/* Header / Hero Section */}
      <section style={{ background: heroBg, padding: '100px 20px 80px', color: '#fff', position: 'relative', overflow: 'hidden' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span style={{ background: 'rgba(255,255,255,0.2)', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', marginBottom: '20px', display: 'inline-block' }}>CORE CAPABILITY</span>
            <h1 style={{ fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', fontWeight: 900, marginBottom: '20px', lineHeight: 1.1 }}>{title}</h1>
            <p style={{ fontSize: 'clamp(1.1rem, 2vw, 1.3rem)', opacity: 0.9, maxWidth: '700px', lineHeight: 1.6 }}>{subtitle}</p>
          </motion.div>
        </div>
        <div style={{ position: 'absolute', bottom: '-50px', right: '-50px', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)', borderRadius: '50%' }} />
      </section>

      {/* Main Narrative Section */}
      <section style={{ padding: '80px 20px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '60px' }}>
          <div>
            <h2 style={{ fontSize: '2rem', fontWeight: 800, color: DARK_SLATE, marginBottom: '25px' }}>Excellence in {title} Execution</h2>
            <div style={{ fontSize: '16px', lineHeight: 1.8, color: '#475569', marginBottom: '30px' }}>
              <p style={{ marginBottom: '20px' }}>{description}</p>
              <p>Operating under strict compliance with Qatar Construction Specifications (QCS 2014) and international ISO safety frameworks, Abraj Al Dhafna ensures every project site adheres to zero-incident policies while maintaining peak productivity rates. We support multi-story commercial footprints in West Bay, high-rise assets in Lusail, and critical industrial systems.</p>
            </div>

            <h3 style={{ fontSize: '1.4rem', fontWeight: 700, color: DARK_SLATE, marginBottom: '20px' }}>Key Service Highlights</h3>
            <ul style={{ listStyle: 'none', padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
              {highlights.map((highlight, i) => (
                <li key={i} style={{ display: 'flex', gap: '12px', alignItems: 'center', fontSize: '15px', color: '#475569' }}>
                  <FaCheckCircle style={{ color: BRAND_BLUE, flexShrink: 0 }} />
                  <span>{highlight}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Side Stats / Summary */}
          <div style={{ background: '#fff', borderRadius: '24px', padding: '40px', boxShadow: '0 10px 30px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0', height: 'fit-content' }}>
            <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: DARK_SLATE, marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f1f5f9' }}>Project Capabilities</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <span style={{ fontSize: '12px', textTransform: 'uppercase', fontWeight: 800, color: '#94a3b8', display: 'block', marginBottom: '5px' }}>Mobilization Threshold</span>
                <strong style={{ fontSize: '18px', color: DARK_SLATE }}>Immediate (Within 24-48 Hours)</strong>
              </div>
              <div>
                <span style={{ fontSize: '12px', textTransform: 'uppercase', fontWeight: 800, color: '#94a3b8', display: 'block', marginBottom: '5px' }}>Compliance Standard</span>
                <strong style={{ fontSize: '18px', color: DARK_SLATE }}>ISO 9001:2015 Certified</strong>
              </div>
              <div>
                <span style={{ fontSize: '12px', textTransform: 'uppercase', fontWeight: 800, color: '#94a3b8', display: 'block', marginBottom: '5px' }}>Deployment Zones</span>
                <strong style={{ fontSize: '18px', color: DARK_SLATE }}>Doha, Lusail, West Bay, Industrial Area</strong>
              </div>
            </div>
            <Link to="/contact" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '40px', background: BRAND_BLUE, color: '#fff', textDecoration: 'none', padding: '16px', borderRadius: '12px', fontWeight: 700, transition: 'transform 0.2s' }}>
              Request A Quote <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* In-Depth Competencies */}
      <section style={{ background: '#fff', padding: '80px 20px', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: 900, color: DARK_SLATE, textAlign: 'center', marginBottom: '50px' }}>Detailed Competency Portfolio</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' }}>
            {coreCompetencies.map((comp, i) => (
              <div key={i} style={{ background: '#f8fafc', padding: '30px', borderRadius: '20px', border: '1px solid #f1f5f9' }}>
                <div style={{ width: '50px', height: '50px', background: `${BRAND_BLUE}10`, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: BRAND_BLUE, fontSize: '1.5rem', marginBottom: '20px' }}>{comp.icon}</div>
                <h4 style={{ fontSize: '1.2rem', fontWeight: 800, color: DARK_SLATE, marginBottom: '10px' }}>{comp.title}</h4>
                <p style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.6 }}>{comp.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {faqs && faqs.length > 0 && (
        <section style={{ padding: '80px 20px' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '2rem', fontWeight: 900, color: DARK_SLATE, textAlign: 'center', marginBottom: '40px' }}>Frequently Asked Questions</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {faqs.map((faq, i) => (
                <div key={i} style={{ background: '#fff', padding: '25px', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.02)', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ fontSize: '1.1rem', fontWeight: 800, color: DARK_SLATE, marginBottom: '10px' }}>{faq.question}</h4>
                  <p style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.6 }}>{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <Cta />
    </main>
  );
};

const CivilSubcontracting = () => {
  const pageData = {
    title: "Civil Subcontracting Services",
    subtitle: "Comprehensive Civil Construction, Concrete Works, and Foundation Management for Qatari Infrastructure.",
    heroBg: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
    description: "Abraj Al Dhafna delivers top-tier civil subcontracting solutions across residential, commercial, and heavy-industrial project landscapes in Qatar. Our deep technical competence spans excavation management, precise structural concrete works (PCC & RCC), robust foundations, reinforcement steel fixing, and finish-grade masonry. Backed by highly skilled masons, carpenters, and steel fixers, we possess the localized infrastructure to scale rapidly and reliably.",
    highlights: [
      "Full Reinforcement Steel Fixing",
      "Advanced PCC & RCC Works",
      "Foundation & Blockwork Masonry",
      "Comprehensive Shuttering Carpentry",
      "High-Grade Demolition & Removal",
      "Structural Modifications & Paving"
    ],
    coreCompetencies: [
      {
        icon: <FaHammer />,
        title: "Structural Concrete & Reinforcement",
        description: "We execute complex formwork, reinforcement, and pouring operations. Our carpenters and steel fixers deliver slabs, columns, and structural beams matching absolute tolerances."
      },
      {
        icon: <FaBuilding />,
        title: "Precision Masonry & Blockwork",
        description: "From massive commercial boundary walls to detailed residential partitioning, our masons maintain impeccable vertical alignment and joint consistency using verified mortar matrices."
      },
      {
        icon: <FaTools />,
        title: "Excavation & Site Preparation",
        description: "Managing initial site mechanics including safe excavation base clearing, leveling, and mass earth stabilization prior to foundational structural laydowns."
      }
    ],
    faqs: [
      {
        question: "Do you provide materials or just skilled manpower?",
        answer: "We offer both model structures! We can operate on a pure skilled labor subcontract basis, or execute complete turnkey subcontracts with physical material and machinery integration depending on the RFQ specification."
      },
      {
        question: "Are your civil crews trained in Qatar Construction Specifications?",
        answer: "Absolutely. Every site supervisor and head mason operates directly under QCS 2014 directives and holds valid local certifications to guarantee compliance audits."
      }
    ]
  };

  return <ServiceDetailPage {...pageData} />;
};

export default CivilSubcontracting;
